import React, { useState, useCallback } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Container,
  useTheme,
} from '@mui/material';
import { Menu as MenuIcon, Login as LoginIcon } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useThemeMode } from '../../common/contexts/ThemeContext';
import ThemeToggle from '../ThemeToggle';
import LoginModal from '../LoginModal';

/**
 * NavigationBar Component
 * 
 * A reusable navigation bar component for public pages (landing page and about page).
 * Features:
 * - Logo that navigates to home
 * - Navigation menu items
 * - Mobile burger menu
 * - Login button
 * - Theme toggle
 * - Login modal integration
 */
interface NavigationBarProps {
  navigationItems?: Array<{ label: string; path: string }>;
  showLogin?: boolean;
}

const NavigationBar: React.FC<NavigationBarProps> = ({
  navigationItems = [],
  showLogin = true,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);

  // Default navigation items if none provided
  const defaultNavItems = [
    { label: 'Home', path: '/' },
    { label: 'About Us', path: '/about' },
  ];

  const navItems = navigationItems.length > 0 ? navigationItems : defaultNavItems;

  const handleNavClick = useCallback((path: string) => {
    navigate(path, { replace: false });
    setMobileMenuOpen(false);
  }, [navigate]);

  const handleLoginClick = useCallback(() => {
    setLoginModalOpen(true);
  }, []);

  const handleLoginSuccess = useCallback(() => {
    setLoginSuccess(true);
  }, []);

  // Navigate after successful login
  React.useEffect(() => {
    if (loginSuccess) {
      const intendedPath = location.state?.from?.pathname || '/dashboard';
      navigate(intendedPath, { replace: true });
      setLoginSuccess(false);
    }
  }, [loginSuccess, navigate, location.state?.from?.pathname]);

  // Show login modal if redirected from protected route
  React.useEffect(() => {
    if (location.state?.from && !loginModalOpen) {
      setLoginModalOpen(true);
    }
  }, [location.state?.from, loginModalOpen]);

  const bgColor = isDark 
    ? 'rgba(0, 0, 0, 0.8)' 
    : 'rgba(255, 255, 255, 0.8)';
  const borderColor = isDark 
    ? 'rgba(255, 255, 255, 0.1)' 
    : 'rgba(0, 0, 0, 0.1)';
  const textColor = isDark ? '#FFFFFF' : '#1D1D1F';

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: bgColor,
          backdropFilter: 'blur(20px)',
          borderBottom: `1px solid ${borderColor}`,
          boxShadow: 'none',
          zIndex: 1000,
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ py: 1, display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            {/* Logo */}
            <Box
              onClick={() => handleNavClick('/')}
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              <img
                src="/images/logo.png"
                alt="Iris Logo"
                style={{
                  height: '40px',
                  width: 'auto',
                  objectFit: 'contain',
                }}
              />
            </Box>

            {/* Desktop Navigation */}
            <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, gap: 2, justifyContent: 'center', mx: 4 }}>
              {navItems.map((item) => (
                <Button
                  key={item.path}
                  onClick={() => handleNavClick(item.path)}
                  sx={{
                    color: textColor,
                    fontWeight: location.pathname === item.path ? 600 : 400,
                    '&:hover': {
                      backgroundColor: isDark 
                        ? 'rgba(255, 255, 255, 0.1)' 
                        : 'rgba(0, 0, 0, 0.05)',
                    },
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>

            {/* Right side actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, ml: 'auto' }}>
              <ThemeToggle />
              {showLogin && (
                <Button
                  variant="contained"
                  startIcon={<LoginIcon />}
                  onClick={handleLoginClick}
                  sx={{
                    display: { xs: 'none', sm: 'flex' },
                    background: 'linear-gradient(135deg, #007AFF 0%, #5856D6 100%)',
                    color: 'white',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #0056CC 0%, #4A4AC7 100%)',
                    },
                  }}
                >
                  Sign In
                </Button>
              )}

              {/* Mobile menu button */}
              <IconButton
                onClick={() => setMobileMenuOpen(true)}
                sx={{
                  display: { xs: 'flex', md: 'none' },
                  color: textColor,
                  ml: { xs: 1, sm: 0 },
                  '&:hover': {
                    backgroundColor: isDark 
                      ? 'rgba(255, 255, 255, 0.1)' 
                      : 'rgba(0, 0, 0, 0.05)',
                  },
                }}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Navigation Drawer */}
      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: 280,
            backgroundColor: isDark ? '#1a1a1a' : '#FFFFFF',
            color: textColor,
            borderRight: `1px solid ${borderColor}`,
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          <List>
            {navItems.map((item) => (
              <ListItem key={item.path} disablePadding>
                <ListItemButton
                  onClick={() => handleNavClick(item.path)}
                  selected={location.pathname === item.path}
                  sx={{
                    borderRadius: 2,
                    mb: 0.5,
                    '&:hover': {
                      backgroundColor: isDark 
                        ? 'rgba(255, 255, 255, 0.1)' 
                        : 'rgba(0, 0, 0, 0.05)',
                    },
                  }}
                >
                  <ListItemText primary={item.label} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* Login Modal */}
      {showLogin && (
        <LoginModal
          open={loginModalOpen}
          onClose={() => setLoginModalOpen(false)}
          onLoginSuccess={handleLoginSuccess}
        />
      )}
    </>
  );
};

export default NavigationBar;

